import React, { memo, useCallback, useContext, useMemo } from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import { ActionsMenu } from "../../../../../components/ActionsMenu/ActionsMenu";
import { RenderDates } from "../../RenderDates/RenderDates";
import ReleaseTextIconMarked from "../../../../../components/VectorIcons/ReleaseTextIconMarked";
import ReleaseTextIcon from "../../../../../components/VectorIcons/ReleaseTextIcon";
import DownloadIcon from "../../../../../components/VectorIcons/DownloadIcon";
import PencilEditIcon from "../../../../../components/VectorIcons/PencilEditIcon";
import DeleteIcons from "../../../../ProjectSheet/ListFiles/BlockList/DeleteIcons/DeleteIcons";
import DeleteTooltip from "../../../../ProjectSheet/ListFiles/DeleteTooltip/DeleteTooltip";
import { PlanningGridContext } from "../../PlanningGrid";
import UserContext from "../../../../../context/userContext";
import useDownloadAttachment from "../../../../../hooks/useDownloadAttachment";
import {
  buildChapterRealizedDates,
  buildChapterReleasePayload,
  buildChapterOtherVersionsPayload,
} from "./ChapterSection.utils";
import CustomTooltip from "../../../../../components/CustomTooltip/CustomTooltip";
import { getReleaseDestinationLabel } from "../../../../../utils";

export const ChapterAttachmentRow = memo(
  ({
    attachment,
    bloco,
    capitulo,
    contentId,
    isChecked,
    toggleOne,
    attachmentActions,
    handleOpenReleaseTexts,
    handleEditFile,
    handleDeleteAttachment,
  }) => {
    const { downloadAttachment } = useDownloadAttachment();
    const { headerGroups, grid, isSelectionMode } =
      useContext(PlanningGridContext);
    const userContext = useContext(UserContext);

    const {
      canDelete,
      getDeleteUnavailableMessage,
      canDownload,
      canReleaseChannel,
      canUnReleaseChannel,
      getReleaseUnavailableMessage,
      getUnReleaseUnavailableMessage,
    } = attachmentActions;

    const hasReleasePermission = userContext.hasPermission({
      required: ["AttachmentRelease"],
    });

    const attachmentId = attachment.attachment?.id || attachment.id;

    const {
      isDeletable,
      deleteUnavailableMessage,
      canDownloadThis,
    } = useMemo(
      () => ({
        isDeletable: canDelete(attachmentId),
        deleteUnavailableMessage: getDeleteUnavailableMessage(attachmentId),
        canDownloadThis: canDownload(attachmentId),
      }),
      [
        attachmentId,
        canDelete,
        getDeleteUnavailableMessage,
        canDownload,
      ]
    );

    const fileToDownload = useMemo(
      () => ({
        fileData: attachment.attachment,
        contentId,
        origin: "capitulos",
        mode: 0,
      }),
      [attachment.attachment, contentId]
    );

    const releaseManagement =
      attachment?.attachment?.releaseManagement;

    const isReleasedToChannel = Boolean(
      releaseManagement?.channel
    );

    const isReleasedToProduction = Boolean(
      releaseManagement?.production
    );

    const isReleasedToCultureHub = Boolean(
      releaseManagement?.cultureHub
    );

    const isReleased =
      isReleasedToChannel || isReleasedToProduction || isReleasedToCultureHub;

    const releaseDestination = getReleaseDestinationLabel({
      channel: isReleasedToChannel,
      production: isReleasedToProduction,
      cultureHub: isReleasedToCultureHub,
    });

    const releaseTooltip = releaseDestination
      ? `Liberado para ${releaseDestination}`
      : "";

    const canReleaseThis = isReleased
      ? canUnReleaseChannel(attachmentId)
      : canReleaseChannel(attachmentId);

    const releaseUnavailableMessage = isReleased
      ? getUnReleaseUnavailableMessage(attachmentId)
      : getReleaseUnavailableMessage(attachmentId);

    const menuOptions = useMemo(
      () => [
        {
          action: () => downloadAttachment(fileToDownload),
          label: "Baixar",
          disabled: !canDownloadThis,
          icon: <DownloadIcon color="#8C8EF5" />,
        },
        {
          action: () =>
            handleOpenReleaseTexts(
              buildChapterReleasePayload(attachment, capitulo, bloco),
              buildChapterOtherVersionsPayload(capitulo, attachment, bloco),
              { type: "planningChapter" }
            ),
          disabled: !hasReleasePermission || !canReleaseThis,
          tooltip: releaseUnavailableMessage ? (
            <DeleteTooltip message={releaseUnavailableMessage} />
          ) : null,
          label: isReleased
            ? "Liberado"
            : "Liberação",
          icon: isReleased ? (
            <ReleaseTextIconMarked />
          ) : (
            <ReleaseTextIcon />
          ),
        },
        {
          action: () =>
            handleEditFile({
              ...attachment.attachment,
              version: attachment.version,
              blockId: capitulo.blockId,
              chapterId: capitulo.id,
              chapterAttachmentId: attachment.id,
              isChapter: true,
            }),
          label: "Editar",
          icon: <PencilEditIcon />,
        },
        {
          action: () => handleDeleteAttachment({ id: attachment.id }, capitulo),
          label: "Remover",
          disabled: !isDeletable,
          tooltip: deleteUnavailableMessage ? (
            <DeleteTooltip message={deleteUnavailableMessage} />
          ) : null,
          icon: (
            <DeleteIcons returnColor={() => (isDeletable ? "#000" : "#CCC")} />
          ),
        },
      ],
      [
        attachment,
        bloco,
        capitulo,
        fileToDownload,
        canDownloadThis,
        isReleased,
        isDeletable,
        deleteUnavailableMessage,
        hasReleasePermission,
        canReleaseThis,
        releaseUnavailableMessage,
        downloadAttachment,
        handleOpenReleaseTexts,
        handleEditFile,
        handleDeleteAttachment,
      ]
    );

    const handleRowClick = useCallback(() => {
      if (!isSelectionMode) return;
      toggleOne(attachment);
    }, [isSelectionMode, toggleOne, attachment]);

    const handleCheckboxChange = useCallback(
      () => toggleOne(attachment),
      [toggleOne, attachment]
    );

    const handleCheckboxClick = useCallback((e) => e.stopPropagation(), []);

    return (
      <Box
        key={`${attachment.chapterId}-${attachment.id}`}
        display="grid"
        gridTemplateColumns={grid}
        sx={{
          borderBottom: "1px solid #F6F6FE",
          minHeight: "52px",
          bgcolor: "#E2E5FD",
        }}
        onClick={handleRowClick}
      >
        {isSelectionMode && (
          <Checkbox
            checked={isChecked}
            onChange={handleCheckboxChange}
            onClick={handleCheckboxClick}
            size="small"
            sx={{
              color: "#43444D",
              "&.Mui-checked": { color: "#43444D" },
            }}
          />
        )}
        <Box
          display="flex"
          alignItems="center"
          gap={3}
          pl={isSelectionMode ? "0" : "16px"}
        >
          <Typography
            sx={{
              fontFamily: "inherit",
              fontSize: "14px",
              color: "#7F808E",
              whiteSpace: "nowrap",
            }}
          >
            {`${attachment.version}ª versão`}
          </Typography>
          <Typography
            sx={{
              fontFamily: "inherit",
              fontSize: "14px",
              color: "#7F808E",
              wordBreak: "break-word",
              pt: "6px",
              pb: "6px",
              pr: "12px",
            }}
          >
            {attachment.attachment.fileName}
          </Typography>
        </Box>

        <RenderDates
          columns={headerGroups}
          planning={bloco.planning}
          realized={buildChapterRealizedDates(attachment)}
          releaseIndicator={
            isReleased && (
              <Box
                sx={{
                  position: "absolute",
                  right: "-18px",
                  top: "14px",
                }}
              >
                <CustomTooltip
                  dataTip={releaseTooltip}
                  tipLabel={
                    <div className="tooltipIcon">
                      <ReleaseTextIconMarked />
                    </div>
                  }
                  theme="light"
                  position="bottom"
                />
              </Box>
            )
          }
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flex: "1",
            pr: "6px",
          }}
        >
          <ActionsMenu menuOptions={menuOptions} disabled={isSelectionMode} />
        </Box>
      </Box>
    );
  }
);

ChapterAttachmentRow.displayName = "ChapterAttachmentRow";
